import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Edit2, Trash2, X, Image as ImageIcon,
  Coins, Tag, CheckCircle2, AlertCircle, MoreVertical, Upload
} from 'lucide-react';
import { giftsAPI } from '../utils/api';

const GiftsManagement = () => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    coin_value: '',
    image_url: '',
    category: 'Common',
    is_active: true
  });

  const filteredGifts = React.useMemo(() => {
    return gifts.filter(gift => 
      gift.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gift.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [gifts, searchTerm]);

  useEffect(() => {
    fetchGifts();
  }, []);

  const fetchGifts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await giftsAPI.getGifts();
      setGifts(response.data.data || []);
    } catch (error) {
      console.error(error);
      setError('Failed to fetch gifts from server.');
      setGifts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    try {
      setUploading(true);
      setError('');
      const response = await giftsAPI.uploadGiftImage(uploadFormData);
      if (response.data.success) {
        setFormData({ ...formData, image_url: response.data.url });
        setSuccess('Image uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error(err);
      setError('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenModal = (gift = null) => {
    if (gift) {
      setSelectedGift(gift);
      setFormData({
        name: gift.name,
        coin_value: gift.coin_value,
        image_url: gift.image_url || '',
        category: gift.category || 'Common',
        is_active: gift.is_active
      });
    } else {
      setSelectedGift(null);
      setFormData({
        name: '',
        coin_value: '',
        image_url: '',
        category: 'Common',
        is_active: true
      });
    }
    setShowModal(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // if (!formData.image_url) {
    //   setError('Please upload an image or provide an image URL');
    //   return;
    // }
    setError('');
    try {
      if (selectedGift) {
        await giftsAPI.updateGift(selectedGift.id, formData);
        setSuccess('Gift updated successfully!');
      } else {
        await giftsAPI.createGift(formData);
        setSuccess('New gift added successfully!');
      }
      setShowModal(false);
      fetchGifts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save gift');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gift?')) {
      try {
        await giftsAPI.deleteGift(id);
        setGifts(gifts.filter(g => g.id !== id));
        setSuccess('Gift deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete gift');
      }
    }
  };

  const toggleStatus = async (gift) => {
    try {
      if (gift.is_active) {
        await giftsAPI.deactivateGift(gift.id);
      } else {
        await giftsAPI.activateGift(gift.id);
      }
      fetchGifts();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gifts Management</h1>
          <p className="text-slate-500 text-sm mt-1">Manage virtual gifts and coin values for your users.</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-pink-600 text-white rounded-xl font-bold hover:bg-pink-700 transition-all shadow-lg shadow-pink-100 active:scale-95"
        >
          <Plus size={18} /> Add New Gift
        </button>
      </div>

      {/* Notifications */}
      {success && (
        <div className="bg-green-50 border border-green-100 text-green-700 px-4 py-3 rounded-xl flex items-center gap-3 animate-in slide-in-from-top duration-300">
          <CheckCircle2 size={18} /> {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 animate-in slide-in-from-top duration-300">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Search & Stats Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search by gift name or category..."
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all outline-none text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-4 items-center border-l pl-4 border-slate-100">
          <div className="text-right">
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Gifts</p>
            <p className="text-lg font-bold text-slate-800">{gifts.length}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-600">
            <Tag size={20} />
          </div>
        </div>
      </div>

      {/* Gifts Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="bg-white rounded-2xl border border-slate-100 p-4 animate-pulse">
              <div className="w-full aspect-square bg-slate-100 rounded-xl mb-4"></div>
              <div className="h-4 bg-slate-100 rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-slate-100 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredGifts.map((gift) => (
            <div key={gift.id} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
              <div className="relative aspect-square bg-slate-50 flex items-center justify-center p-6 overflow-hidden">
                <img
                  src={gift.image_url || 'https://via.placeholder.com/200?text=No+Image'}
                  alt={gift.name}
                  className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 flex gap-1 transform translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300">
                  <button
                    onClick={() => handleOpenModal(gift)}
                    className="p-2 bg-white text-slate-700 rounded-lg shadow-lg hover:text-pink-600 transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(gift.id)}
                    className="p-2 bg-white text-slate-700 rounded-lg shadow-lg hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur px-2 py-1 rounded-md text-[10px] font-bold text-slate-600 uppercase border border-white">
                  {gift.category}
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-bold text-slate-800">{gift.name}</h3>
                  <div className="flex items-center gap-1 text-yellow-600 font-bold text-sm">
                    <Coins size={14} /> {gift.coin_value}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => toggleStatus(gift)}
                    className={`text-xs font-bold px-3 py-1 rounded-full transition-colors ${gift.is_active
                      ? 'bg-green-50 text-green-600 hover:bg-green-100'
                      : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                      }`}
                  >
                    {gift.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {!loading && filteredGifts.length === 0 && (
        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
          <div className="w-16 h-16 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-800">No gifts found</h3>
          <p className="text-slate-500">Try adjusting your search or add a new gift.</p>
        </div>
      )}

      {/* Upsert Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md max-h-[90vh] rounded-3xl shadow-2xl relative z-10 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <h2 className="text-xl font-bold text-slate-800">{selectedGift ? 'Edit Gift' : 'Add New Gift'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              {/* Scrollable Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-slate-200">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Gift Name</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all"
                    placeholder="e.g. Heart, Diamond, Rose"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Coin Value</label>
                    <div className="relative">
                      <Coins className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="number"
                        required
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all"
                        placeholder="0"
                        value={formData.coin_value}
                        onChange={(e) => setFormData({ ...formData, coin_value: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">Category</label>
                    <select
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="Common">Common</option>
                      <option value="Rare">Rare</option>
                      <option value="Epic">Epic</option>
                      <option value="Legendary">Legendary</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">Image URL</label>
                  <div className="relative">
                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                      type="url"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500 outline-none transition-all text-xs"
                      placeholder="Or paste URL here..."
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    />
                  </div>
                </div>

                {formData.image_url && (
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-slate-100 mx-auto">
                    <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl-lg"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}

                <div className="flex items-center gap-3 py-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                    className={`w-12 h-6 rounded-full relative transition-colors ${formData.is_active ? 'bg-pink-600' : 'bg-slate-200'}`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.is_active ? 'left-7' : 'left-1'}`}></div>
                  </button>
                  <span className="text-sm font-medium text-slate-600">Active Status</span>
                </div>
              </div>

              {/* Modal Footer (Always Visible) */}
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-200"
                >
                  {selectedGift ? 'Save Changes' : 'Create Gift'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftsManagement;